import {debounceTime, distinctUntilChanged, filter, map, switchMap, take, tap} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DOCUMENT} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  ActivationStart,
  ChildActivationStart,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import {EMPTY, fromEvent, interval} from 'rxjs';
import {ModalRef, ToastContainerComponent} from '@sms-ir/ui-components';

import {HelpService} from '@shared/components/help/services/help.service';
import {LoadingService} from '@shared/services/loading.service';
import {ConfigService} from '@shared/services/config.service';
import {StoreService} from '@shared/services/store.service';
import {ModalService} from '@shared/services/modal.service';
import {ColorSchemeService} from '@shared/services/color-scheme.service';
import {DeviceType} from '@shared/models/device-type';
import {environment} from '@env/environment';

@UntilDestroy()
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastContainerComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  prevUrl!: string;
  currentUrl!: string;
  modalRef!: ModalRef;
  private lastDeviceMode: DeviceType | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private storeService: StoreService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private modalService: ModalService,
    private helpService: HelpService,
    private configService: ConfigService,
    private loadingService: LoadingService,
    private http: HttpClient,
    private colorSchemeService: ColorSchemeService,
  ) {
    this.loadingService.setModalLoadingState(true);
    void this.colorSchemeService.currentTheme;

    this.router.events
      .pipe(map(m => {
        if (m instanceof NavigationStart) {
          this.prevUrl = this.router.url.split('?')[0];
        }
        if ((m instanceof ChildActivationStart) || (m instanceof ActivationStart && m.snapshot.data.hasOwnProperty('helpItem'))) {
          this.helpService.helpId = m.snapshot.data && m.snapshot.data.helpItem ? m.snapshot.data.helpItem : 0;
          if (this.helpService.helpItems) {
            const payload = this.helpService.helpItems?.find(item => item.id == this.helpService.helpId);
            this.helpService.selectHelp(payload);
          }
        }
        return m;
      }))
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(map((m) => {
        const url = (m as NavigationEnd).urlAfterRedirects;
        this.currentUrl = url.split('?')[0];
        return m;
      }))
      .pipe(
        map(() => this.activatedRoute),
        map(route => {
          const allComponents: ActivatedRouteSnapshot[] = [];
          let routeParams = {...route.snapshot.queryParams};
          const sameUrl = this.prevUrl === this.currentUrl;
          if (routeParams.hasOwnProperty('needCount') && !sameUrl) {
            delete routeParams.needCount;
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: routeParams,
            }).then();
          }

          while (route) {
            if (!!(route.component) && !!(route.snapshot.data.title)) {
              allComponents.push(route.snapshot);
            }
            (<ActivatedRoute | null>route) = route.firstChild;
          }
          return allComponents;
        }),
        map((res) => {
          return res.map(i => i.data.title);
        })
      )
      .subscribe(res => {
        const title = res.length > 0 ? `SMS.ir | ${res[res.length - 1]}` : 'SMS.ir';
        this.titleService.setTitle(title);
      });
  }

  ngOnInit() {
    this.loadingService.modalLoadingState$.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      switchMap((state: boolean) => {
        if (state) {
          this.modalRef = this.modalService.loading();
          return this.modalRef.afterClosed$.pipe(take(1));
        } else {
          this.modalRef?.close();
          return EMPTY;
        }
      }),
    ).subscribe()

    this.resize(window.innerWidth);
    fromEvent(window, 'resize')
      .pipe(
        untilDestroyed(this),
        debounceTime(300),
      )
      .subscribe((res: any) => {
        this.resize(res.target['innerWidth']);
      });

    this.checkForUpdates();
  }

  ngOnDestroy() {
  }

  resize(width: number) {
    let device: DeviceType;
    if (width <= parseInt(this.configService.deviceWidth.mobile.max, 10)) {
      device = 'mobile';
    } else if (width >= parseInt(this.configService.deviceWidth.tablet.min, 10) &&
      width <= parseInt(this.configService.deviceWidth.tablet.max, 10)) {
      device = 'tablet';
    } else {
      device = 'desktop';
    }

    if (device !== this.lastDeviceMode) {
      this.storeService.initSidebarMode(device);
      this.lastDeviceMode = device;
      this.storeService.setDeviceMode(device);
    }
  }

  checkForUpdates() {
    if (!environment.production) {
      return;
    }
    interval(60000).pipe(
      untilDestroyed(this),
      switchMap(() => {
        return this.http.get('./version.json?t=' + Date.now())
      }),
      map(resp => (resp as { forceUpdate: boolean, version: string })),
      tap((resp: { forceUpdate: boolean, version: string }) => {
        this.storeService.setHasUpdate(environment.appVersion != resp.version);

        if (environment.appVersion != resp.version && resp.forceUpdate) {
          window.location.reload();
        }
      }),
    ).subscribe()
  }
}
